import { createSelector } from 'reselect/lib/index'

import {
  getApiContacts,
  getApiPublicContacts,
  getContactsLookup,
  getPublicContactsLookup,
} from '../api/selectors'
import { getCurrentTeammateHandle } from '../current/selectors'

export const LATEST_DIRECT_CHAT_LIMIT = 10

export const makeGetContact = () =>
  createSelector(
    [getApiContacts, getContactsLookup, (_, props) => props.handle],
    (contacts, contactsLookup, handle) => {
      const id = contactsLookup && contactsLookup[handle]
      return id && contacts?.[id]
    },
  )

export const getLatestContacts = createSelector(
  [getApiContacts, getCurrentTeammateHandle],
  (contacts, currentHandle) => {
    const filteredContacts = Object.values(contacts).filter((contact) => {
      return (
        contact.blocked_status !== 'blocked' &&
        !contact.removed &&
        !contact.from_widget
      )
    })

    const sortedContacts = filteredContacts
      .sort((contactA, contactB) => {
        if (contactA.handle === currentHandle) {
          return -1
        }
        if (contactB.handle === currentHandle) {
          return 1
        }
        return (
          Date.parse(contactB.last_messaged_at || contactB.created_at) -
          Date.parse(contactA.last_messaged_at || contactA.created_at)
        )
      })
      .sort((contactA, contactB) => {
        if (contactA.direct_chat_id && contactB.direct_chat_id) {
          return 0
        }
        if (contactA.direct_chat_id) {
          return -1
        }
        if (contactB.direct_chat_id) {
          return 1
        }

        return 0
      })
    const firstX = sortedContacts.splice(0, LATEST_DIRECT_CHAT_LIMIT)

    firstX.sort((contactA, contactB) => {
      if (contactA.handle === currentHandle) {
        return -1
      }
      if (contactB.handle === currentHandle) {
        return 1
      }
      return contactA.account_name.toUpperCase() >
        contactB.account_name.toUpperCase()
        ? 1
        : -1
    })
    return firstX
  },
)

export const makeGetAlphabeticalContacts = () =>
  createSelector(
    [
      getApiContacts,
      getCurrentTeammateHandle,
      (_, { selfAtTop } = { selfAtTop: false }) => selfAtTop,
    ],
    (contacts, currentHandle, selfAtTop) => {
      if (!Object.keys(contacts)) {
        return []
      }

      const filteredContacts = Object.values(contacts).filter((contact) => {
        return (
          contact.blocked_status !== 'blocked' &&
          !contact.removed &&
          !contact.from_widget
        )
      })

      return filteredContacts.sort((contactA, contactB) => {
        if (selfAtTop) {
          if (contactA.handle === currentHandle) {
            return -1
          }
          if (contactB.handle === currentHandle) {
            return 1
          }
        }
        return contactA.account_name.toUpperCase() >
          contactB.account_name.toUpperCase()
          ? 1
          : -1
      })
    },
  )

export const makeGetNamesForHandles = () =>
  createSelector(
    [
      getApiPublicContacts,
      getApiContacts,
      getPublicContactsLookup,
      getContactsLookup,
      (_, handles) => handles,
    ],
    (
      publicContacts,
      contacts,
      publicContactsLookup,
      contactsLookup,
      handles,
    ) => {
      return handles.reduce((acc, handle) => {
        const publicContactId = publicContactsLookup?.[handle]
        const publicContact = publicContacts[publicContactId]
        if (publicContact?.name) {
          acc[handle] = publicContact.name
        } else {
          const contactId = contactsLookup?.[handle]
          acc[handle] = contacts[contactId]?.account_name || handle
        }
        return acc
      }, {})
    },
  )

export const getContactsCount = createSelector([getApiContacts], (contacts) => {
  return Object.values(contacts)?.length
})
